import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useCurrentAccount } from "../../../hooks/useCurrentAccount"
import { ReportService } from "../../../services/ReportsService"
import { AlertService } from "../../../services/Alert";
import { ColorUtil } from "../../../utils/color"
import { NumberUtil } from "../../../utils/number";
import Select from 'react-select';
import SemiDonutChart from "../../../components/SemiDonutChart";
import BarCHar from "../../../components/BarChart";
import Aside from "../../../components/Aside"
import Footer from "../../../components/Footer"
import Header from "../../../components/Header"
import DatePicker from "../../../components/form-controls/DatePicker";
import useRequest from "../../../hooks/useRequest"
import LoaderSmall from "../../../components/LoaderSmall"
import Loader from '../../../components/Loader';
import moment from 'moment';



function ReportConversionPage() {

    const { t } = useTranslation();
    const { call, isRequesting } = useRequest();
    const { account } = useCurrentAccount();
    const heightRequesting = useMemo(() => { return isRequesting ? '' : '' }, [isRequesting]);
    const [isDonutChartRequesting, setIsDonutChartRequesting] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isSpecificReportRequesting, setIsSpecificReportRequesting] = useState(false);
    const [tipificationsRequesting, setTipificationsRequesting] = useState(false);
    const [filterRangeDates, setFilterRangeDates] = useState({ start: moment().subtract(29, 'days'), end: moment() });
    const [search, setSearch] = useState<any[]>([]);
    const [selectedSearchId, setSelectedSearchId] = useState<any>('');
    const [typeSearch, setTypeSearch] = useState<any>('');
    const [totalSum, setTotalSum] = useState(0);
    const [activeTab, setActiveTab] = useState('tabOne');
    const [tipifications, setTipifications] = useState<{ labels: string[], data: number[] }>({
        labels: [],
        data: []
    });
    const [chartData, setChartData] = useState({
        labels: [],
        data: [],
        backgroundColor: []
    });


    const [totalTipifications, setTotalTipifications] = useState(0);
    const [rcsEmpty, setRcsEmpty] = useState(false);
    const [cardsConversions, setCardsConversions] = useState({
        totalSends: 0,
        totalConvertions: 0,
        conversionsRCS: 0,
        conversionsSMS: 0,
        conversionsWAB: 0,
        rateRCS: 0,
        rateSMS: 0,
        rateWAB: 0,
        successGeneral: 0,
        successSMS: 0,
        successRCS: 0,
        successWAB: 0,
        errorGeneral: 0,
        errorSMS: 0,
        errorRCS: 0,
        errorWAB: 0
    });

    const getStatistics = async () => {
        if (!filterRangeDates.start || !filterRangeDates.end) return;
        const init_date = filterRangeDates.start.format('YYYY-MM-DD');
        const end_date = filterRangeDates.end.format('YYYY-MM-DD');
        setIsDonutChartRequesting(true);
        setIsSpecificReportRequesting(true);
        setTipificationsRequesting(true);
        try {
            const response = await call(() => ReportService.getConversions(account.id, init_date, end_date));

            if (response?.data?.[0]) {
                const { totals, convertions, response_rate, sendings } = response.data[0];
                const dataValues = Object.values(response.data[1]) as number[];
                const total = dataValues.reduce((acc, value) => acc + value, 0);
                setCardsConversions({
                    totalSends: totals?.total_sends ?? 0,
                    totalConvertions: totals?.total_convertions ?? 0,
                    conversionsRCS: convertions?.RCS ?? 0,
                    conversionsSMS: convertions?.SMS ?? 0,
                    conversionsWAB: convertions?.["WA-Business"] ?? 0,
                    rateRCS: parseFloat(response_rate?.RCS) ?? 0,
                    rateSMS: parseFloat(response_rate?.SMS) ?? 0,
                    rateWAB: parseFloat(response_rate?.["WA-Business"]) ?? 0,
                    successGeneral: totals?.total_success ?? 0,
                    successSMS: sendings?.SMS?.sents ?? 0,
                    successRCS: sendings?.RCS?.sents ?? 0,
                    successWAB: sendings?.['WA-Business']?.sents ?? 0,
                    errorGeneral: totals?.total_failed ?? 0,
                    errorSMS: sendings?.SMS?.failed ?? 0,
                    errorRCS: sendings?.RCS?.failed ?? 0,
                    errorWAB: sendings?.['WA-Business']?.failed ?? 0,
                }); 
                const rcsExists = convertions?.RCS !== undefined && response_rate?.RCS !== undefined;
                setRcsEmpty(rcsExists);
                setTipifications({
                    labels: Object.keys(response.data[1]),
                    data: Object.values(response.data[1])
                });
                setTotalTipifications(total);

                const uniqueOptions = Array.from(
                    new Set(response.data[2].map((item: any) => item.input_key))
                ).map((inputKey) => ({
                    value: inputKey,
                    label: inputKey
                }));
                setSearch(uniqueOptions);
                if (uniqueOptions.length > 0) {
                    setSelectedSearchId(uniqueOptions[0].value);
                    setTypeSearch(uniqueOptions[0].value)
                }
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setIsDonutChartRequesting(false);
            setIsSpecificReportRequesting(false);
            setTipificationsRequesting(false);
        }
    };

    const getSpecificReport = async () => {
        const init_date = filterRangeDates.start.format('YYYY-MM-DD');
        const end_date = filterRangeDates.end.format('YYYY-MM-DD');
        const task = activeTab === 'tabOne' ? 'sum' : 'group';
        setIsSpecificReportRequesting(true);
        try {
            const response = await call(() => ReportService.getSpecificConversion(account.id, init_date, end_date, task, typeSearch));
            if (task === 'sum') {
                setTotalSum(response.data.data)
            } else {
                const group = response.data.data;
                const labels = Object.keys(group);
                const data = Object.values(group);
                setChartData({
                    labels,
                    data,
                    backgroundColor: tipifications.labels.map(l => ColorUtil.stringToRGB(l)),
                });
            }
        } catch (error) {
            console.error(t('reports.convertion.error'), error);
        } finally {
            setIsSpecificReportRequesting(false);
        }
    };

    const handleTabActive = (tab: string) => {
        setActiveTab(tab);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                await getStatistics();
                if (!isFirstLoad) {
                    await getSpecificReport();
                }
            } catch (error: any) {
                AlertService.toastError(t('reports.convertion.error'), error);
            } finally {
                setIsFirstLoad(false);
            }
        };
        fetchData();
    }, [filterRangeDates]);

    useEffect(() => {
        const fetchSpecificReport = async () => {
            try {
                if (typeSearch) {
                    await getSpecificReport();
                }
            } catch (error: any) {
                AlertService.toastError(t('reports.convertion.error'), error);
            }
        };

        fetchSpecificReport();
    }, [typeSearch, activeTab]);

    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">

                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("reports.operation-title")}</span></li>
                                        <li className="breadcrumb-item " aria-current="page">{t("reports.reports-title")}</li>
                                        <li className="breadcrumb-item active" aria-current="page">{t("reports.convertion.convertion-title")}</li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title"><i className="bi-grid-1x2 nav-icon"></i> {t("reports.reports-title")} - {t("reports.convertion.convertion-title")}</h1>
                            </div>
                            <div className="col-sm-auto">
                                <DatePicker onDatesChange={setFilterRangeDates} />
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 mb-lg-5">
                        <div className={`row gx-lg-6 relative ${heightRequesting}`}>
                            {isDonutChartRequesting ? (
                                <LoaderSmall />
                            ) : (
                                <>
                                    <div className={`col-lg-${rcsEmpty ? '3' : '4'}`}>
                                        <div className="card h-100">
                                            <div className="card-header bg-soft-primary">
                                                <h4 className="card-header-title">
                                                    Total general
                                                </h4>
                                            </div>
                                            <div className="card-body bg-icon-card" style={{
                                                backgroundImage: `linear-gradient(rgba(255, 255, 255, .6), rgba(255, 255, 255, 5)), url('/assets/img/conversion-totals.png')`
                                            }}>
                                                <ul className="nav nav-pills card-nav card-nav-vertical nav-pills">
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-send-fill fw-bold fs-2" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.totalSends)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.sends")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-chat-left-text-fill fw-bold fs-3" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.totalConvertions)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.card-conversations")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi bi-check-circle-fill fw-bold fs-2 text-primary" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.successGeneral)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.success")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li> <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-x-circle-fill fw-bold fs-2 text-danger" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.errorGeneral)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.errors")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`col-lg-${rcsEmpty ? '3' : '4'}`}>
                                        <div className="card h-100">
                                            <div className="card-header bg-soft-primary">
                                                <h4 className="card-header-title">
                                                    SMS
                                                </h4>
                                            </div>
                                            <div className="card-body bg-icon-card-b" style={{
                                                backgroundImage: `linear-gradient(rgba(255, 255, 255, .6), rgba(255, 255, 255, 5)), url('/assets/img/sms-conversion.png')`
                                            }}>
                                                <ul className="nav nav-pills card-nav card-nav-vertical nav-pills">
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-chat-left-text-fill fw-bold fs-2" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.conversionsSMS)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.card-conversations")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-graph-up-arrow fw-bold fs-3" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.rateSMS)}%
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.card-rate")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi bi-check-circle-fill fw-bold fs-2 text-primary" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.successSMS)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.success")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li> <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-x-circle-fill fw-bold fs-2 text-danger" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.errorSMS)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.errors")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    {rcsEmpty && (
                                        <div className={`col-lg-${rcsEmpty ? '3' : '4'}`}>
                                            <div className="card h-100">
                                                <div className="card-header bg-soft-primary">
                                                    <h4 className="card-header-title">
                                                        RCS
                                                    </h4>
                                                </div>
                                                <div className="card-body bg-icon-card-b" style={{
                                                    backgroundImage: `linear-gradient(rgba(255, 255, 255, .6), rgba(255, 255, 255, 5)), url('/assets/img/conversion-rcs.png')`
                                                }}>
                                                    <ul className="nav nav-pills card-nav card-nav-vertical nav-pills">
                                                        <li>
                                                            <a
                                                                className="nav-link"
                                                                href="#"
                                                            >
                                                                <div className="d-flex">
                                                                    <div className="flex-shrink-0">
                                                                        <i className="bi bi-chat-left-text-fill fw-bold fs-2" />
                                                                    </div>
                                                                    <div className="flex-grow-1 ms-3">
                                                                        <span className="d-block text-dark fw-bolder fs-4">
                                                                            {NumberUtil.format(cardsConversions.conversionsRCS)}
                                                                        </span>
                                                                        <small className="d-block fs-5 fw-bold text-primary">
                                                                            {t("reports.convertion.card-conversations")}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a
                                                                className="nav-link"
                                                                href="#"
                                                            >
                                                                <div className="d-flex">
                                                                    <div className="flex-shrink-0">
                                                                        <i className="bi bi-graph-up-arrow fw-bold fs-3" />
                                                                    </div>
                                                                    <div className="flex-grow-1 ms-3">
                                                                        <span className="d-block text-dark fw-bolder fs-4">
                                                                            {NumberUtil.format(cardsConversions.rateRCS)}%
                                                                        </span>
                                                                        <small className="d-block fs-5 fw-bold text-primary">
                                                                            {t("reports.convertion.card-rate")}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a
                                                                className="nav-link"
                                                                href="#"
                                                            >
                                                                <div className="d-flex">
                                                                    <div className="flex-shrink-0">
                                                                        <i className="bi bi bi-check-circle-fill fw-bold fs-2 text-primary" />
                                                                    </div>
                                                                    <div className="flex-grow-1 ms-3">
                                                                        <span className="d-block text-dark fw-bolder fs-4">
                                                                            {NumberUtil.format(cardsConversions.successRCS)}
                                                                        </span>
                                                                        <small className="d-block fs-5 fw-bold text-primary">
                                                                            {t("reports.convertion.success")}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        </li> <li>
                                                            <a
                                                                className="nav-link"
                                                                href="#"
                                                            >
                                                                <div className="d-flex">
                                                                    <div className="flex-shrink-0">
                                                                        <i className="bi bi-x-circle-fill fw-bold fs-2 text-danger" />
                                                                    </div>
                                                                    <div className="flex-grow-1 ms-3">
                                                                        <span className="d-block text-dark fw-bolder fs-4">
                                                                            {NumberUtil.format(cardsConversions.errorRCS)}
                                                                        </span>
                                                                        <small className="d-block fs-5 fw-bold text-primary">
                                                                            {t("reports.convertion.errors")}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className={`col-lg-${rcsEmpty ? '3' : '4'}`}>
                                        <div className="card h-100">
                                            <div className="card-header bg-soft-primary">
                                                <h4 className="card-header-title">
                                                    WhatsApp Business
                                                </h4>
                                            </div>
                                            <div className="card-body bg-icon-card-b" style={{
                                                backgroundImage: `linear-gradient(rgba(255, 255, 255, .6), rgba(255, 255, 255, 5)), url('/assets/img/wa-businness.png')`
                                            }}>
                                                <ul className="nav nav-pills card-nav card-nav-vertical nav-pills">
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-chat-left-text-fill fw-bold fs-2" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.conversionsWAB)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.card-conversations")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-graph-up-arrow fw-bold fs-3" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.rateWAB)}%
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.card-rate")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi bi-check-circle-fill fw-bold fs-2 text-primary" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.successWAB)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.success")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li> <li>
                                                        <a
                                                            className="nav-link"
                                                            href="#"
                                                        >
                                                            <div className="d-flex">
                                                                <div className="flex-shrink-0">
                                                                    <i className="bi bi-x-circle-fill fw-bold fs-2 text-danger" />
                                                                </div>
                                                                <div className="flex-grow-1 ms-3">
                                                                    <span className="d-block text-dark fw-bolder fs-4">
                                                                        {NumberUtil.format(cardsConversions.errorWAB)}
                                                                    </span>
                                                                    <small className="d-block fs-5 fw-bold text-primary">
                                                                        {t("reports.convertion.errors")}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <span className="divider-center mb-7"> {t("reports.convertion.results")}</span>
                    {/* Resultados generales */}
                    <div className="row">
                        <div className="col-lg-4 mb-3 mb-lg-5">
                            <div className="card">
                                <div className="card-header card-header-content-between">
                                    <h4 className="card-header-title">
                                        {t('dashboard-operations.results')}
                                    </h4>
                                </div>
                                <div className="card-body text-center">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="text-start">
                                        </div>
                                        <div className="h4 fw-bold">
                                            <span className="text-primary rounded-pill">
                                                {t('dashboard-operations.tipifications')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-center mb-0">
                                        <div className={`relative ${heightRequesting}`}>
                                            {
                                                tipificationsRequesting ? <Loader /> :
                                                    <>
                                                        {tipifications.labels.length !== 0 ? (
                                                            <div className="d-flex flex-column align-items-center">
                                                                <SemiDonutChart
                                                                    datasetName="Resumen"
                                                                    showLegend={false}
                                                                    dataset={{
                                                                        labels: tipifications.labels,
                                                                        data: tipifications.data,
                                                                        backgroundColor: tipifications.labels.map(l => ColorUtil.stringToRGB(l)),
                                                                    }}
                                                                />
                                                                <div className="mt-0">
                                                                    <h3 className="text-primary">{t("reports.convertion.total-tipifications")}</h3>
                                                                    <div className="h1 text-primary">
                                                                        {NumberUtil.format(totalTipifications)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="alert my-5" role="alert">
                                                                <div className="d-flex flex-column justify-content-center align-items-center">
                                                                    <div className="flex-shrink-0">
                                                                        <img
                                                                            alt="Image Description"
                                                                            className="avatar avatar-xl"
                                                                            src="/assets/svg/illustrations/oc-browse.svg"
                                                                        />
                                                                        <h3 className="alert-heading mb-1">
                                                                            {t('dashboard-operations.empty')}
                                                                        </h3>
                                                                        <p className="mb-0">
                                                                            {t('dashboard-operations.no-data')}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-8 mb-3 mb-lg-5">
                            <div className={`relative ${heightRequesting}`}>

                                <div className="card">
                                    <div className="card-header card-header-content-between">
                                        <h4 className="card-header-title">
                                            {t("reports.convertion.sum-group")}
                                        </h4>
                                    </div>
                                    <div className="card-body text-center">

                                        <div className="row align-items-center mb-7">


                                            <div className="col-4 text-start">
                                                <Select
                                                    options={search}
                                                    noOptionsMessage={() => (
                                                        <div className="empty-select">{t('general.no-options')}</div>
                                                    )}
                                                    className="form-select-custom"
                                                    classNamePrefix="form-select-custom"
                                                    placeholder={t("reports.convertion.placeholder-select")}
                                                    onChange={(selectedOption: { value: string; label: string } | null) => {
                                                        if (selectedOption) {
                                                            setSelectedSearchId(selectedOption.value);
                                                            setTypeSearch(selectedOption.value);
                                                        }
                                                    }}
                                                    value={search.find(
                                                        (searchValue: any) => searchValue.value === selectedSearchId
                                                    )}
                                                />
                                            </div>
                                            <div className="col-8 text-end h4 fw-bold">
                                                <ul className="nav nav-segment nav-pills">
                                                    <li className="nav-item cursor-pointer">
                                                        <a className={`nav-link ${activeTab === 'tabOne' ? 'active' : ''}`}
                                                            onClick={() => handleTabActive('tabOne')}
                                                        >
                                                            <i className="bi bi-plus-circle-fill text-primary"></i>
                                                            {' '}{t("reports.convertion.tab-one")}
                                                        </a>
                                                    </li>
                                                    <li className="nav-item cursor-pointer">
                                                        <a className={`nav-link ${activeTab === 'tabTwo' ? 'active' : ''}`}
                                                            onClick={() => handleTabActive('tabTwo')}>
                                                            <i className="bi bi-bar-chart-line-fill text-primary"></i>
                                                            {' '}{t("reports.convertion.tab-two")}
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>

                                        </div>
                                        <div className="text-center mb-0">
                                            {
                                                isSpecificReportRequesting ? <Loader /> :
                                                    <>
                                                        {activeTab === 'tabOne' ? (
                                                            <div className="row align-items-center mb-3 justify-content-center">
                                                                <div className="col-auto text-start">
                                                                    <img
                                                                        alt="Image Description"
                                                                        className="avatar avatar-xl"
                                                                        src="/assets/img/total-2.png"
                                                                    />
                                                                </div>
                                                                <div className="col-auto">
                                                                    <span className="display-3 text-dark me-4">{NumberUtil.format(totalSum)}</span>
                                                                    <span className="d-block display-6 text-primary fw-bolder">{t("reports.convertion.total-sum")}</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <BarCHar
                                                                datasetName="Total"
                                                                legendDisplay={false}
                                                                dataset={chartData}
                                                            />
                                                        )}
                                                    </>}
                                        </div>

                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
                <Footer />
            </main>
        </>
    )
}
export default ReportConversionPage